// The head of the page when the address names one device (#329): the device
// is the page, so its name is the title, one line under it says what it is,
// and its state and its acts sit where a reader looks first rather than in a
// table row under the identity panel.
//
// Nothing here is a second read or a second computation. The words come from
// the fields and hooks the site's table already reads — `Device.online` and
// `presence` through `statusOf`, the convergence read through `runningOf`
// (§11.2, ADR-0022 decision 3), the catalogue match through the one device
// read the page shares with the detail (ADR-0036) — and the buttons are the
// row's own `DeviceActions`, behind the page's own gates. One definition of
// online, shared by every view. A switch's *Design* word is `designOf`'s over
// the site's work list, the read and the function the Switches list uses.
//
// **The device's front is at the head too** (#589, `DeviceFaceHead`): the
// faceplate, drawn once above the sections rather than again by the Ports
// tab and the Links tab, from the reads the page already makes and handed
// down; the set of ports it gathers is the page's, read by every tab. A
// switch's sits under the title and above the identity, so the operator sees
// the hardware before the sections about it; an access point's is a thumbnail
// that opens it whole (#1889), inside the identity card (#1894, `DeviceDetail`).
import { useId, useRef, useState, type ReactNode } from "react";
import { plural } from "../../dashboard/format";
import { Icon, Pill, When } from "../../ui";
import type { Device, FirmwareDelivery } from "./commands";
import { CdsChipButton, CdsPopover } from "./CdsRecord";
import { DeliveryMarker } from "./delivery";
import DeviceActions, { type ActionHandlers } from "./DeviceActions";
import DeviceFace, { FaceLegend, type FacePhoto, type FacePort, type FaceSelection, type PortState } from "./DeviceFace";
import { ModelWords, type useDeviceModel, type useLiveState } from "./DeviceDetail";
import { pushOf, runningOf, statusOf, type StateWords, type Tone, type useConvergence } from "./DevicesTable";
import { LiveLine, type ReportTimes } from "./Ports";
import { WORK_LIST_PATH } from "./family";
import { ScopeLink } from "../../shell/scope";
import { categoryWord } from "../../api/openEnum";

/** The line under the name: the family, what the device reports itself as
 * (one string as the gateway carries it, never split into a vendor and a
 * model — §11.2), the catalogue model or why there is none, and the cell it
 * is read from. A part that is not known yet is left out rather than drawn
 * as a placeholder; the line says what it has.
 *
 * **These are said here and nowhere else on the page** (#1801): the identity
 * panel under the face used to say the family, the hardware, the model and
 * the controller again, and now carries the facts this line does not. */
export function DeviceSub({ device, cellId, one, showsCds = false }: {
  device: Device; cellId: string; one: ReturnType<typeof useDeviceModel>;
  /** The superuser flag (#2118): whether the line ends with the CDS chip, drawn only where the
   * device's own read says the CDS reading needs attention (`CdsChipButton`). */
  showsCds?: boolean;
}) {
  const family = categoryWord(device.category, true);
  return (
    <>
      <span>{family}</span>
      {device.manufacturer && <> · <span title="what the device reports itself as, one string as the controller carries it; not the catalog model">{device.manufacturer}</span></>}
      {/* Restates ADR-0036 — the catalogue model is matched from the device's own declared ports, never its type string.
          ADR-0193 put the model string the device declares first, so the words say both (#1801 moved
          the identity panel's sentence here, where the model is now said once). */}
      {one.match && <> · <span title="the catalog model the device's own declared model string names, or else the one its declared ports match; never read from its type string"><ModelWords match={one.match} /></span></>}
      {" "}· Controller <code>{cellId}</code>
      {showsCds && one.read && <>{" "}<CdsChipButton read={one.read} onReread={one.reload} /></>}
    </>
  );
}

/** A state's words as a pill: a dot and the word, never colour alone. */
function StatePill({ words: w }: { words: StateWords }) {
  return <Pill tone={w.tone ?? "none"} title={w.title}>{w.word}</Pill>;
}

/** Whether Design holds intent for the device that no apply has carried
 * (#1615): `designOf`'s word for it, from the site's work list, labelled
 * *Design* so it is never read as the device's state, and a link to the work
 * list in Design where it names something to apply. It is never *up to date*:
 * an absent item is *nothing listed* (ADR-0117 decision 1). */
// Restates ADR-0117 decision 1 and ADR-0149 decision 1a — the word is the work list's, and the
// way to act on it is the work list in Design, never an apply on this page.
export function DesignPill({ words, linked }: { words: StateWords; linked: boolean }) {
  const pill = <StatePill words={words} />;
  return (
    <span className="head-design">
      <span className="dim">Design</span>{" "}
      {linked && words.tone ? <ScopeLink to={WORK_LIST_PATH} title={words.title}>{pill}</ScopeLink> : pill}
    </span>
  );
}

/** The status pill's words while the device's own read is out: not a state,
 * and never one borrowed from another read (§9.4, ADR-0234). */
const READING: StateWords = { word: "reading…", title: "Whether the controller is talking to this device, from the device's own read of its controller, which has not answered yet." };
/** Its words when that read failed: nothing is known, and nothing is shown as known. */
const NOT_READ: StateWords = { word: "not read", title: "The device's own read of its controller did not answer, so whether the controller is talking to it is not shown." };

/** The status pill's words, from the device's own read alone (#1802). */
// Restates ADR-0234 decision 1 (`1c`) and ZENITH.md §9.4 — a device's own page reads its
// liveness from the controller, through `GET /devices/{serial}`, and never from the list's copy.
// Until #1802 the pill read the copy's row, which the name, the facts and the acts still come from.
function ownStatusOf(own: { status: string; read?: Device }): StateWords {
  if (own.read) return statusOf(own.read);
  return own.status === "error" ? NOT_READ : READING;
}

export default function DeviceHead({ device, own, convergence, busy, mayCommand, mayConsole, mayPoeCycle, change, onRefresh, onAct, onConsole, design, designReady = false, delivery, cds }: ActionHandlers & {
  device: Device;
  /** The device's own read (`GET /devices/{serial}`) and its state: the only
   * read the status pill is drawn from. The page draws everything else in the
   * head from `device`, the list's row, so nothing waits on this read (#1802). */
  own: { status: string; read?: Device };
  convergence: ReturnType<typeof useConvergence>;
  busy: boolean;
  mayCommand: boolean; mayConsole: boolean;
  /** Where the device's intent is changed — its *Change this in Design*, for a
   * caller who may (#875) — drawn after the acts it keeps here. */
  change?: ReactNode;
  /** A switch's Design word, `designOf`'s, while the site's work list is read
   * (#1615); absent for an access point and while the read is out. */
  design?: StateWords;
  /** Whether the work list answered, so a word that names something to apply links to it. */
  designReady?: boolean;
  /** The device's newest firmware delivery, which marks the head while it is under way or its
   * outcome is not known (ADR-0403 `1a`, `3a`). Absent is not read or none. */
  delivery?: FirmwareDelivery;
  onRefresh(): void;
  /** The superuser's *CDS record…* in Actions (#2118): reads the device again once CDS has been re-read.
   * Absent for everyone else, and then the menu carries no such item. */
  cds?: { onReread(): void };
}) {
  const status = ownStatusOf(own);
  // The CDS record's popover from the Actions menu (#2118), anchored at the menu's trigger, which the
  // menu gives the focus back to when the item is chosen. The same popover as the chip's.
  const actions = useRef<HTMLButtonElement>(null);
  const [cdsOpen, setCdsOpen] = useState(false);
  // **One chip, three facts kept apart inside it** (#1889, Gabriel Provencher's review of the
  // 2026-10-20 mockup board, concept 2: *"there are too many statuses"*). Until then the head drew
  // three pills, deliberately not merged (#413): whether the cell is talking to the device, where
  // Zenith's last write to it got to, and what the device reports itself to be running. ZENITH.md
  // §10.2 — *applied* is about the controller and *synchronized* is about the device — is why they
  // were apart, and it is why they stay apart in the chip's breakdown (`StatusChip`): the chip says
  // the device's state in one word or two, and the breakdown says each fact in the words `onlineOf`,
  // `PUSH` and `convergenceWord` give, never merged into one sentence. A write that needs attention
  // keeps a marker beside the chip, as a firmware delivery under way does (ADR-0403 `1a`). A switch's
  // *Design* word is a fourth fact (#1615) and stays its own labelled pill: whether Design holds
  // intent no apply has carried, which none of the three can say.
  //
  // **The running state is said only beside an `online` status, and never when its own state is
  // `offline`** (#1846). The head says whether the controller is talking to the device once, from
  // the device's own read. The running state is §11.2's, and §11.2 reads its `offline` from §9.4's
  // one definition before any generation is compared — through the convergence read's per-device
  // status read, a second read of that one definition with its own cache and its own age (ADR-0219
  // Fact 3, decision 1 `1a`: both reads stay). Said together, the two answered a reboot with
  // "online" and "offline" side by side, and beside a status that is not online any running verdict
  // says the same contradiction in other words. So nothing here decides liveness: it only chooses
  // not to say a state the API already computed, and the status word carries the case.
  const row = convergence.data?.devices.find((d) => d.serial === device.serial);
  const online = status.word === "online";
  const running = online && row?.state !== "offline" ? runningOf(device.serial, convergence) : undefined;
  // Restates ADR-0485 decisions 1a, 2a and 3a — the pill beside the status is drawn only for an
  // outcome that still describes the device. One the convergence read says the device has settled
  // past, or the unattended re-fetch's, is `settled` and neutral, so no pill: its words stay in the
  // status chip's breakdown below, with its time.
  const push = pushOf(device, convergence);
  const at = device.last_push?.finished_at ?? device.last_push?.started_at;
  // One status line, then the acts (#1801, #1889): the console and Refresh, Actions ▾ for the
  // commands, and the one way to Design last. The page's head lays the two out, not this span
  // (`display: contents` in theme.css): the status beside the name, the acts on a row that wraps
  // (#1894).
  return (
    <span className="page-actions device-head">
      <span className="head-status">
        <StatusChip status={status} push={push} pushedAt={at} held={row?.held} running={running} saysRunning={online} />
        {push && isMarkedTone(push.tone) && <StatePill words={push} />}
        {design && <DesignPill words={design} linked={designReady} />}
        <DeliveryMarker delivery={delivery} />
      </span>
      <span className="head-acts">
        <DeviceActions as="head" device={device} busy={busy} mayCommand={mayCommand} mayConsole={mayConsole} mayPoeCycle={mayPoeCycle} onAct={onAct} onConsole={onConsole}
          beside={<button type="button" className="btn btn-quiet btn-icon" onClick={onRefresh}><Icon name="refresh" /><span>Refresh</span></button>}
          trigger={actions} onCdsRecord={cds && own.read ? () => setCdsOpen(true) : undefined} />
        {cds && own.read && <CdsPopover open={cdsOpen} anchor={actions} onClose={() => setCdsOpen(false)} read={own.read} onReread={cds.onReread} />}
        {change}
      </span>
    </span>
  );
}

const isMarkedTone = (t?: Tone) => t === "warn" || t === "critical";

/** The chip is the house pill, drawn on a button: a dot and the word, never colour alone. */
const CHIP_CLASS: Record<Tone | "none", string> = { none: "pill pill-none", good: "pill pill-good", warn: "pill pill-warn", critical: "pill pill-critical" };

const SEVERITY: Record<Tone | "none", number> = { none: 0, good: 1, warn: 2, critical: 3 };

/** The chip's word and tone (#1889): the status word, and beside an `online` one the running word
 * after a middle dot — `online · current`, `online · behind`, `online · changed outside` — so the
 * chip says the state in the words the head said it in before, never in new ones. Its tone is the
 * more severe of the two, so `online · behind` is critical and `offline` stays untoned (ADR-0040).
 * `unknown` is its own word and never `offline`, and no percentage is drawn (§9.4, ADR-0219). The
 * status words are `statusOf`'s, `reading…` and `not read`; the running words are
 * `convergenceWord`'s and `not judged`. */
export function chipOf(status: StateWords, running?: StateWords): { word: string; tone?: Tone } {
  if (!running) return { word: status.word, tone: status.tone };
  const tone = SEVERITY[running.tone ?? "none"] > SEVERITY[status.tone ?? "none"] ? running.tone : status.tone;
  return { word: `${status.word} · ${running.word}`, tone };
}

/** The device's one status chip and its breakdown (#1889). Hover or focus shows the three facts
 * apart, each in the words its own function gives and with that function's sentence: the
 * connection (`statusOf`, from the device's own read); what the controller holds and where
 * Zenith's last write got to (`pushOf`, with the generation the convergence read says the
 * controller holds and when the write ended); and what the device runs (`runningOf`). The chip is a
 * button, so the keyboard reaches it; Escape and leaving it close the breakdown. */
export function StatusChip({ status, push, pushedAt, held, running, saysRunning }: {
  status: StateWords; push?: StateWords; pushedAt?: string; held?: number; running?: StateWords;
  /** Whether the status is `online`, so the breakdown says what the device runs (#1846). */
  saysRunning: boolean;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const chip = chipOf(status, running);
  const pill = CHIP_CLASS[chip.tone ?? "none"];
  return (
    <span className="status-chip" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className={pill} aria-describedby={open ? id : undefined}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}>
        <span className="dot" aria-hidden="true" />{chip.word}
      </button>
      {open && (
        <span id={id} role="tooltip" className="status-breakdown">
          <span className="breakdown-row">
            <span className="breakdown-k">Connection</span>
            <span className="breakdown-v">{status.word}</span>
            {status.title && <span className="breakdown-says">{status.title}</span>}
          </span>
          <span className="breakdown-row">
            <span className="breakdown-k">Controller</span>
            <span className="breakdown-v">
              {held !== undefined && <>holds generation {held}{push && " · "}</>}
              {push ? <>{push.word}{pushedAt && <> · <When value={pushedAt} /></>}</> : held === undefined && <span className="muted">—</span>}
            </span>
            {push?.title && <span className="breakdown-says">{push.title}</span>}
          </span>
          {saysRunning && (
            <span className="breakdown-row">
              <span className="breakdown-k">Device runs</span>
              <span className="breakdown-v">{running ? running.word : <span className="muted">—</span>}</span>
              {running?.title && <span className="breakdown-says">{running.title}</span>}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/** The ports to draw and what is painted on each — `portsOf`'s answer for a
 * switch, the catalogue's ports with the cabled layer for an access point. */
export type Face = { ports: FacePort[]; states: Record<string, PortState> };

/** The device's front at the head of its page (#589): the faceplate drawn
 * once, above the tabs, with every layer the Ports tab painted when it drew
 * its own — the intent, the live link and power, the uplink, a plan's
 * change, the cabled links, and the set the operator gathers (#467) — and
 * pressed once for every tab: `selection` is the page's, the Ports tab's
 * detail opens for it, the Links tab names its link — and on Design › Wired's
 * Ports tab, where the same face heads the editor (#875), the editor
 * describes it. Under the face, what
 * is pressed, then for a switch the live read's own line (#514) — the layer it
 * explains is here now — and the one legend. A switch whose ports nothing
 * enumerates yet has the line alone: the read's answer is what says why
 * there is nothing to draw. */
export function DeviceFaceHead({ device, name, face, photo, selection, caption, live, times, legend, mayConfigure = false }: {
  device: Device;
  /** The name the page shows for the device (#1907); the device's own where left out. */
  name?: string;
  /** The face to draw; none for a device whose ports nothing enumerates. */
  face?: Face;
  photo?: FacePhoto;
  /** The page's set of pressed ports and how it changes. */
  selection: FaceSelection;
  /** What the drawing is: the model, the count, the uplink — the caller's words. */
  caption?: ReactNode;
  /** A switch's live read and when it was read, the page's own (#371, #514); an access point has none. */
  live?: ReturnType<typeof useLiveState>;
  times?: ReportTimes;
  /** Which layers the legend explains, as the caller knows them. */
  legend: { change?: boolean; live?: boolean; links?: boolean; uplink?: boolean; intent?: boolean };
  /** Whether the bulk editor is on the page under the face — Design › Wired's
   * Ports tab — so the hint can name it; a device's page in Operate has none. */
  mayConfigure?: boolean;
}) {
  const liveLine = live && times ? <LiveLine live={live} times={times} /> : null;
  if (!face || face.ports.length === 0) return liveLine && <div className="face-head">{liveLine}</div>;
  const { ports, states } = face;
  const picked = selection.ports;
  const single = picked.length === 1 ? picked[0] : undefined;
  const chosen = single ? states[single] : undefined;
  const isSwitch = device.category === "switch";
  return (
    <section className="panel face-head" aria-label="Faceplate">
      <div className="face-wrap">
        <DeviceFace ports={ports} states={states} selection={selection}
          label={`${name ?? device.name}, ${ports.length} ${plural(ports.length, "port")}`} photo={photo} fit />
      </div>
      <p className="face-selected" aria-live="polite">
        {single
          ? <><code>{single}</code> · {chosen?.detail ?? (chosen?.uplink ? "the uplink; no planned settings are stored for it" : chosen?.cabled ? `cabled to ${chosen.cabled}` : "no planned settings")}</>
          : picked.length > 1
            ? <>{picked.length} selected: {picked.map((n, i) => <span key={n}>{i > 0 && ", "}<code>{n}</code></span>)}</>
            : <><span className="muted">{isSwitch ? "Select a port to view its planned settings." : "Select a port to read what is cabled to it."}</span>{mayConfigure && isSwitch && <span className="muted"> Select several to edit them together in the editor below.</span>}</>}
      </p>
      {liveLine}
      <FaceLegend {...legend} />
      {caption && <p className="muted small">{caption}</p>}
    </section>
  );
}
